import { Injectable, NgZone, Inject, PLATFORM_ID, Optional } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ANALYTICS_CONFIG, AnalyticsConfig, AnalyticsDefaultParameters, AnalyticsSdk } from '@app/shared/interface/skd-analytics';

declare global {
  interface Window {
    _containerGTM?: string;
    SDKAnalytics?: AnalyticsSdk;
  }
}

@Injectable({ providedIn: 'root' })
export class SdkAnalyticsService {
  private sdk: AnalyticsSdk | null = null;
  private loadPromise: Promise<void> | null = null;
  private isBrowser = false;

  // Guarda a configuração efetiva vinda do environment (ou override)
  private currentGtmId: string | undefined;
  private currentSdkUrl: string | undefined;
  private currentDefaultParameters: AnalyticsDefaultParameters | undefined;

  private queue: Array<() => void> = [];

  constructor(
    private zone: NgZone,
    @Optional() @Inject(ANALYTICS_CONFIG) private cfg: AnalyticsConfig | null,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  public loadSdk(
    gtmId?: string,
    sdkUrlOverride?: string,
    initializeParameters?: AnalyticsDefaultParameters
  ): Promise<void> {
    if (!this.isBrowser) {
      return Promise.resolve();
    }

    // Se já iniciamos o load, devolve a promise existente
    if (this.loadPromise !== null) {
      return this.loadPromise;
    }

    // Resolva os valores efetivos
    let url = sdkUrlOverride || (this.cfg ? this.cfg.sdkUrl : '');
    let gtm = gtmId || (this.cfg ? this.cfg.gtmId : '');
    let defaultParameters = initializeParameters || (this.cfg ? this.cfg.defaultParameters : undefined);

    // Persiste os valores efetivos
    this.currentSdkUrl = url;
    this.currentGtmId = gtm;
    this.currentDefaultParameters = defaultParameters;

    // Seta antes de carregar o script
    if (gtm) {
      window._containerGTM = gtm;
    }

    // Caminho rápido: factory global já existe ou script já foi injetado
    let factory: any = (window as any).SDKAnalytics;
    let existing = document.querySelector('script[src="' + url + '"]') as HTMLScriptElement | null;

    let onReady = () => {
      if (defaultParameters) {
        this.initializeDefaultParameters(defaultParameters);
      }
      this.flushQueue();
    };

    if (typeof factory === 'function' || existing) {
      this.loadPromise = this.waitUntilReady().then(onReady);
      return this.loadPromise;
    }

    // Inserir script dinamicamente fora da zona
    let self = this;
    this.loadPromise = new Promise<void>(function (resolve, reject) {
      self.zone.runOutsideAngular(function () {
        let script = document.createElement('script');
        script.src = url;
        script.async = true;
        if (self.cfg && self.cfg.scriptNonce) {
          script.nonce = String(self.cfg.scriptNonce);
        }
        script.onload = function () {
          self.waitUntilReady().then(function () {
            self.zone.run(function () {
              onReady();
              resolve();
            });
          }).catch(function (err) {
            self.zone.run(function () { reject(err); });
          });
        };
        script.onerror = function () {
          self.zone.run(function () { reject(new Error('Erro ao carregar SDK Analytics: ' + url)); });
        };
        document.head.appendChild(script);
      });
    });

    return this.loadPromise;
  }

  private get timeoutMs(): number {
    return (this.cfg && typeof this.cfg.timeoutMs === 'number') ? this.cfg.timeoutMs : 10000;
  }

  private isSdkReady(): boolean {
    if (!this.isBrowser) {
      return false;
    }

    let factory: any = (window as any).SDKAnalytics;

    // Somente instancia quando tivermos um GTM efetivo.
    if (!this.sdk && typeof factory === 'function') {
      let gtmToUse: any =
        this.currentGtmId ||
        (typeof window._containerGTM === 'string' ? window._containerGTM : undefined);

      if (!gtmToUse) {
        // Ainda não sabemos o container correto -> não instancia
        return false;
      }

      try {
        this.sdk = factory({
          gtm: gtmToUse,
          webview: !!(this.cfg && this.cfg.webview),
          engagement: this.cfg && this.cfg.engagement ? this.cfg.engagement : null,
          fullstory: this.cfg && this.cfg.fullstory ? this.cfg.fullstory : null,
          allowlist: true,
          dual_tagging: true
        });
      } catch (e) {
        // noop
      }
    }

    let api: any = this.sdk;
    return !!api &&
      typeof api.track === 'function' &&
      typeof api.setUserProperties === 'function' &&
      typeof api.setDefaultParameters === 'function';
  }

  private waitUntilReady(timeout: number = this.timeoutMs): Promise<void> {
    if (!this.isBrowser) return Promise.resolve();
    if (this.isSdkReady()) return Promise.resolve();

    let self = this;
    return new Promise<void>(function (resolve, reject) {
      self.zone.runOutsideAngular(function () {
        let started = (window.performance && performance.now) ? performance.now() : Date.now();
        let timer = window.setInterval(function () {
          if (self.isSdkReady()) {
            window.clearInterval(timer);
            self.zone.run(function () { resolve(); });
          } else {
            let now = (window.performance && performance.now) ? performance.now() : Date.now();
            if ((now - started) > timeout) {
              window.clearInterval(timer);
              self.zone.run(function () { reject(new Error('Timeout aguardando SDK pronto (métodos indisponíveis).')); });
            }
          }
        }, 50);
      });
    });
  }

  public initializeDefaultParameters(defaultParameter: AnalyticsDefaultParameters): void {
    if (!defaultParameter) {
      return;
    }
    let call = () => {
      if (this.sdk && this.sdk.setDefaultParameters) {
        this.sdk.setDefaultParameters({ analytics: { parameters: defaultParameter } });
      }
    };
    if (this.sdk) call();
    else this.queue.push(call);
  }

  public trackEvent(eventName: string, eventParams: any): void {
    this.callSdk(sdk => {
      sdk.track({ analytics: { event_name: eventName, parameters: eventParams } });
    });
  }

  public setUserProperties(properties: any): void {
    this.callSdk(sdk => {
      sdk.setUserProperties({ analytics: properties });
    });
  }

  public setDefaultParameters(parameters: AnalyticsDefaultParameters): void {
    this.callSdk(sdk => {
      sdk.setDefaultParameters({ analytics: { parameters: parameters } });
    });
  }

  public removeUserProperties(): void {
    this.callSdk((sdk) => {
      if (sdk.removeUserProperties) {
        sdk.removeUserProperties();
      }
    });
  }

  public removeDefaultParameter(parameter: string): void {
    this.callSdk((sdk) => {
      if (sdk.removeDefaultParameter) {
        sdk.removeDefaultParameter(parameter);
      }
    });
  }

  public removeDefaultParameters(parameters: string[]): void {
    this.callSdk((sdk) => {
      if (sdk.removeDefaultParameters) {
        sdk.removeDefaultParameters(parameters);
      }
    });
  }

  whenReady(): Promise<void> {
    if (!this.isBrowser) return Promise.resolve();
    if (this.loadPromise === null) {
      let cfg = this.cfg;
      let gtm = this.currentGtmId || (typeof window._containerGTM === 'string' ? window._containerGTM : (cfg ? cfg.gtmId : undefined));
      let url = this.currentSdkUrl || (cfg ? cfg.sdkUrl : undefined);
      let defaults = this.currentDefaultParameters || (cfg ? cfg.defaultParameters : undefined);
      this.loadPromise = this.loadSdk(gtm, url, defaults).catch(function () { /* swallow */ });
    }
    return this.loadPromise as Promise<void>;
  }

  private flushQueue(): void {
    if (!this.queue.length) return;

    if (!this.isSdkReady()) {
      this.waitUntilReady()
        .then(() => this.flushQueue())
        .catch(() => { /* swallow */ });
      return;
    }

    let tasks = this.queue.slice();
    this.queue.length = 0;

    this.zone.runOutsideAngular(() => {
      for (let i = 0; i < tasks.length; i++) {
        try { tasks[i](); } catch (e) { /* swallow */ }
      }
    });
  }

  private callSdk(fn: (sdk: AnalyticsSdk) => void): void {
    if (this.isSdkReady()) {
      this.zone.runOutsideAngular(() => {
        try { fn(this.sdk as AnalyticsSdk); } catch (e) { /* swallow */ }
      });
      return;
    }

    // Carregamento sob demanda (ainda privilegiando valores efetivos)
    if (this.loadPromise === null && this.isBrowser) {
      let cfg = this.cfg;
      let gtm = this.currentGtmId || (typeof window._containerGTM === 'string' ? window._containerGTM : (cfg ? cfg.gtmId : undefined));
      let url = this.currentSdkUrl || (cfg ? cfg.sdkUrl : undefined);
      let defaults = this.currentDefaultParameters || (cfg ? cfg.defaultParameters : undefined);
      this.loadSdk(gtm, url, defaults).catch(function () { /* swallow */ });
    }

    if (this.queue.length >= 100) {
      this.queue.shift();
    }

    this.queue.push(() => {
      if (!this.isSdkReady()) {
        this.flushQueue();
        return;
      }
      try { fn(this.sdk as AnalyticsSdk); } catch (e) { /* swallow */ }
    });
  }
}
